<template>

  <div class="padded">

    <xolas-table
      table-ref="utiltable"
      :title="tableTitle"
      icon="fa fa-plug"
      :url="'/utility/site/' + billsKey"
      :fields="fields"
      :show-search="showSearch"
      layer-component="bill"
      :layer-args="{ id: 'id' }">

      <template v-if="canAccess('finance|admin') && effectiveRowData" #actions>
        <utility-action button-size="small" :site-data="effectiveRowData" />
      </template>

      <template #controls="props">
        <el-button
          v-if="canDeleteBill(props.rowData)"
          icon="fa fa-trash-alt"
          size="small"
          type="danger"
          circle
          @click.stop="deleteEntry(props.rowData)" />
      </template>

    </xolas-table>

  </div>

</template>


<script>
import axios from '@/lib/axiosClient'

import {settings} from '@/components/Data'

import DateMixins from '@/mixins/DateMixins'
import NumberMixins from '@/mixins/NumberMixins'

import UtilityAction from '@/components/actions/UtilityBillAction'

export default {
  mixins: [DateMixins, NumberMixins],

  components: {
    UtilityAction
  },

  props: {
    rowData: {
      type: Object,
      default: null
    },

    utilityId: {
      type: Number,
      default: null
    },

    title: {
      type: String,
      default: 'Utility Bills'
    },

    showSearch: {
      type: Boolean,
      default: true
    },

    readOnly: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      fetchedRowData: null,
      label: this.getUtilLabel(this.rowData),

      fields: [
        {
          name: '__sequence',
          title: '#',
          width: 70,
          align: 'center'
        },
        {
          name: 'date',
          title: 'Date',
          callback: 'formatDate|D MMM YYYY',
          minWidth: 130
        },
        {
          name: 'amount',
          title: settings.utilAmountLabel,
          callback: this.formatNumber,
          titleClass: 'hidden-sm-and-down',
          dataClass: 'hidden-sm-and-down text-right',
          minWidth: 110,
          align: 'right'
        },
        {
          name: 'tax',
          title: settings.taxName,
          visible: this.showTax(),
          titleClass: 'hidden-md-and-down',
          dataClass: 'hidden-md-and-down text-right',
          minWidth: 90,
          align: 'right'
        },
        {
          name: 'total',
          title: 'Total',
          dataClass: 'text-right',
          callback: this.formatNumber,
          minWidth: 110,
          align: 'right'
        },
        {
          name: 'paid',
          title: 'Paid',
          callback: this.formatBoolean,
          titleClass: 'hidden-sm-and-down',
          dataClass: 'hidden-sm-and-down',
          width: 80,
          align: 'center'
        },
        {
          name: 'paidDate',
          title: 'Paid Date',
          callback: 'formatDate|D MMM YYYY',
          titleClass: 'hidden-md-and-down',
          dataClass: 'hidden-md-and-down',
          minWidth: 120
        },
        {
          name: '__slot:controls',
          title: '',
          width: 80,
          align: 'center'
        }
      ]

    }
  },  // data

  computed: {
    effectiveRowData () {
      return this.rowData || this.fetchedRowData
    },

    billsKey () {
      // Prefer the URL param (utilityId) — it's stable across the whole
      // lifecycle of this component. Falling back to rowData.id was causing
      // the URL to recompute mid-render: SiteUtilTable.mounted() fetches
      // /api/sites/<id>, the response shape on this endpoint doesn't always
      // carry a top-level `id`, so `effectiveRowData.id` resolves to
      // `undefined` and the DataTable re-fires against /utility/site/undefined,
      // clobbering the already-loaded rows.
      if (this.utilityId) return this.utilityId
      if (this.effectiveRowData && this.effectiveRowData.id) return this.effectiveRowData.id
      return null
    },

    tableTitle: function () {
      return this.title ? this.title : this.label + ' Bills'
    }
  },

  mounted () {
    if (!this.rowData && this.utilityId) {
      // /api/sites/{id} returns a Laravel paginator wrapper (`{ data: [...],
      // current_page, total }`) AND the controller filters internally by
      // `organisation_id` against the caller's org — so cross-org sites
      // return EMPTY even with `attachOrg=false`. Bills are accessible
      // cross-org via /api/utility/site/{siteId} (see SiteUtilBillDetail
      // wrapper), but the SITE row itself is not reachable as a single fetch
      // by id today.
      //
      // Fallback to a minimal `{ id }` shim so the actions slot's add-bill
      // popup at least opens with the right site_id. Full site fields
      // (tenancy.utilAccount, etc.) won't pre-populate the form in the
      // cross-org case — flag as a backend follow-up to add a `?withSite=1`
      // option to /api/utility/site/{siteId} that includes the site object,
      // or a dedicated /api/sites/{id}?global=1 super-only endpoint.
      const minimalShim = {
        id: this.utilityId,
        // Match the shape UtilityBillDetails.data() needs at init — it reads
        // siteData.tenancy.utilAddress / .utilAccount synchronously and will
        // throw "Cannot read of undefined" if `tenancy` is missing.
        tenancy: { utilAddress: '', utilAccount: '' }
      }
      axios.get(this.getEndpoint('sites/' + this.utilityId, null, false))
        .then(res => {
          const list = res.data && Array.isArray(res.data.data) ? res.data.data : null
          const fromList = (list && list.length > 0) ? list[0] : null
          const fromShow = (res.data && !list) ? res.data : null
          const payload = fromList || fromShow || minimalShim
          // Ensure tenancy is always present for downstream consumers.
          if (!payload.tenancy) payload.tenancy = minimalShim.tenancy
          this.fetchedRowData = payload
          this.label = this.getUtilLabel(payload)
        })
        .catch(() => {
          this.fetchedRowData = minimalShim
        })
    }
  },

  methods: {
    canDeleteBill (bill) {
      // Match the legacy UtilityControls gate: managers can delete unpaid
      // bills, supers can delete any.
      return (this.isManager() && !bill.paid) || this.isSuper()
    },

    deleteEntry (bill) {
      this.$confirm('Delete this bill?', 'Confirm', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        axios.delete(this.getEndpoint('utility/' + bill.id)).then(() => {
          this.$message.success('Bill deleted')
          this.$events.emit('utiltable-refresh-table')
        })
      }).catch(() => { /* user cancelled */ })
    }
  }
}

</script>
